/**
 * PRODUÇÃO — `FilterComposer`: a variante SIMPLES do `Composer` para o filtro por texto das
 * páginas de resultados (Figma 1060:22860 / 1061:24213). Mesma casca do composer do chat —
 * `InputGroup`, os addons (`ADDON`), o canto direito (`SendOrDictate`), a barra do ditado
 * (`ComposerDictation`) e o aviso dele (`DictationStatus`) — SEM as camadas do chat: nada
 * de menções, anexo, typewriter, sugestões ou expansão no foco. O campo é um input nativo
 * de uma linha; o ✦ à esquerda diz que o texto vira filtro, não mensagem.
 *
 * A casca tem dois estados, decididos pelo CONTEÚDO e não pelo foco: vazia é a pílula
 * (`rounded-full`); com `chips` ou com aviso vira o card (`rounded-3xl`, a mesma
 * `shellShape` do composer do chat). Os chips são um slot — quem os desenha é o consumidor
 * (o rótulo NASCE de i18n dele, e o kit não tem copy) — e moram DENTRO da casca, acima da
 * linha do campo, como no Figma. Este arquivo não posiciona nada na página: onde a casca
 * gruda (pé da coluna, folha) é decisão de quem a monta.
 */
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type SubmitEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../ui/input-group';
import {
  ADDON,
  ComposerDictation,
  DictationStatus,
  SendOrDictate,
  onShellKeyDown,
  useDictationFlow,
  type ComposerLabels,
  type DictationCaptureHook,
  type Status,
} from './composer';
import { shellShape } from './composer_shell';

export type FilterComposerLabels = Pick<
  ComposerLabels,
  | 'input'
  | 'send'
  | 'dictate'
  | 'dictationCancel'
  | 'dictationStop'
  | 'dictationSend'
  | 'transcribing'
  | 'dictationFailed'
>;

/** Um aviso do consumidor na faixa sob o campo ("Aplicando seu filtro…", "Isso é uma
 *  pergunta — faça no chat"). O aviso do ditado (transcrevendo / falha) tem precedência. */
export type FilterComposerNote = { text: string; error?: boolean | undefined };

export interface FilterComposerProps {
  /** O texto aparado. O campo limpa depois de enviar; vazio não envia. */
  onSubmit: (text: string) => void;
  /** Os chips do consumidor, desenhados dentro da casca acima do campo. Ausente ou vazio,
   *  a casca é a pílula. */
  chips?: ReactNode;
  note?: FilterComposerNote | undefined;
  /** Um pedido em voo: o envio trava (o campo segue editável). */
  busy?: boolean | undefined;
  /** Campo e controles inertes (resultado congelado). */
  disabled?: boolean | undefined;
  /** Liga o ditado: o mic ganha ação/atalho e a casca troca pra barra do ditado. */
  dictation?: { useCapture: DictationCaptureHook } | undefined;
  labels: FilterComposerLabels;
  /** No invólucro (o `<form>`). */
  className?: string | undefined;
  maxLength?: number | undefined;
}

/* O `maxLength` do campo só trava o que é DIGITADO: o ditado entra por aqui, então é
   aqui que o teto vale para ele também. */
const join = (draft: string, dictated: string, limit: number | undefined): string => {
  const clean = dictated.trim();
  if (!clean) return draft;
  const merged = draft === '' ? clean : `${draft} ${clean}`;
  return limit === undefined ? merged : merged.slice(0, limit);
};

/* O texto e a entrega do ditado. Refs para o rascunho e a trava porque a colheita
   assíncrona resolve em OUTRO render: lê o estado da ENTREGA, não o do gesto (a trava
   `busy`/`disabled` pode ter chegado no meio — #2688). */
function useFilterText(
  onSubmit: (text: string) => void,
  locked: boolean,
  maxLength: number | undefined,
) {
  const [text, setText] = useState('');
  const textRef = useRef(text);
  const lockedRef = useRef(locked);
  const onSubmitRef = useRef(onSubmit);
  const limitRef = useRef(maxLength);
  // Renovadas a cada commit: quem as lê (o envio, a colheita) roda depois dele.
  useLayoutEffect(() => {
    textRef.current = text;
    lockedRef.current = locked;
    onSubmitRef.current = onSubmit;
    limitRef.current = maxLength;
  });
  const apply = useCallback((raw: string) => {
    const clean = raw.trim();
    if (!clean || lockedRef.current) return;
    onSubmitRef.current(clean);
    setText('');
  }, []);
  const send = useCallback(() => {
    apply(textRef.current);
  }, [apply]);
  // O ditado appenda ao rascunho (padrão do composer do chat), nunca o substitui.
  const deliverFill = useCallback((dictated: string) => {
    setText((draft) => join(draft, dictated, limitRef.current));
  }, []);
  const deliverSend = useCallback(
    (dictated: string) => {
      apply(join(textRef.current, dictated, limitRef.current));
    },
    [apply],
  );
  return { text, setText, send, deliverFill, deliverSend };
}

function Field({
  inputRef,
  text,
  setText,
  labels,
  disabled,
  busy,
  maxLength,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  text: string;
  setText: (text: string) => void;
  labels: FilterComposerLabels;
  disabled: boolean;
  busy: boolean;
  maxLength: number | undefined;
}) {
  return (
    <>
      <InputGroupAddon className={cn(ADDON, 'pl-3.5')}>
        <SparkleIcon weight="fill" className="text-foreground size-4" aria-hidden />
      </InputGroupAddon>
      <InputGroupInput
        ref={inputRef}
        type="text"
        value={text}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={labels.input}
        aria-label={labels.input}
        aria-busy={busy}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
    </>
  );
}

export function FilterComposer({
  onSubmit,
  chips,
  note,
  busy = false,
  disabled = false,
  dictation,
  labels,
  className,
  maxLength,
}: Readonly<FilterComposerProps>) {
  const locked = busy || disabled;
  const inputRef = useRef<HTMLInputElement>(null);
  const micRef = useRef<HTMLButtonElement>(null);
  const stopRef = useRef<HTMLButtonElement>(null);
  const { text, setText, send, deliverFill, deliverSend } = useFilterText(
    onSubmit,
    locked,
    maxLength,
  );
  const focusField = useCallback(() => {
    inputRef.current?.focus();
  }, []);
  const voice = useDictationFlow({ labels, deliverFill, deliverSend, focusField, micRef });
  const canDictate = Boolean(dictation) && !disabled && !voice.busy;
  const status: Status =
    voice.status ?? (note ? { text: note.text, error: note.error ?? false } : null);
  const hasChips = chips != null && chips !== false;
  const shape = shellShape(false, status !== null, hasChips);
  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    send();
  };
  return (
    <form
      className={cn('relative', className)}
      onSubmit={submit}
      onKeyDownCapture={(e) => {
        onShellKeyDown(e, voice, canDictate, stopRef);
      }}
    >
      {/* A mesma casca do `Composer` (borda, raio, sombra, botões redondos) e o anel de
          foco do Figma (2px `--ring`) no lugar do anel difuso do `InputGroup`. */}
      <InputGroup
        className={cn(
          'bg-card ring-foreground/10 relative h-auto min-h-13 flex-col items-stretch border-0 ring-1 shadow-(--shadow-soft) [&_button]:rounded-full',
          'has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-ring',
          shape,
        )}
      >
        {hasChips ? <div className="px-4 pt-3">{chips}</div> : null}
        <div className="flex min-h-[inherit] w-full items-center">
          {voice.dictating && dictation ? (
            <ComposerDictation
              useCapture={dictation.useCapture}
              labels={labels}
              onCancel={voice.cancel}
              onStop={voice.stop}
              onSend={voice.sendDictated}
              onError={voice.fail}
              stopRef={stopRef}
            />
          ) : (
            <>
              <Field
                inputRef={inputRef}
                text={text}
                setText={setText}
                labels={labels}
                disabled={disabled}
                busy={busy}
                maxLength={maxLength}
              />
              <SendOrDictate
                hasText={text.trim() !== ''}
                disabled={locked}
                micDisabled={!canDictate}
                send={send}
                onDictate={canDictate ? voice.start : undefined}
                labels={labels}
                micRef={micRef}
              />
            </>
          )}
        </div>
        <DictationStatus status={status} />
      </InputGroup>
    </form>
  );
}
